<template>
  <div class="tidal-id-field">
    <label :for="fieldId" class="form-label">
      Tidal Album ID
      <span class="optional-indicator">(Optional)</span>
    </label>
    
    <div class="tidal-input-wrapper">
      <input
        :id="fieldId"
        v-model="localValue"
        type="text"
        class="form-input"
        :class="{ 
          'error': hasError 
        }"
        placeholder="Enter Tidal album ID or leave blank"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
      />
      
      <!-- Status indicators -->
      <div class="input-status">
        <div v-if="isValidating" class="validating-indicator">
          <span class="loading-spinner"></span>
          <span class="validating-text">Validating...</span>
        </div>
        
        <div v-if="isValid && localValue && !isValidating" class="valid-indicator">
          <span class="valid-badge">✓ Valid</span>
        </div>
      </div>
      
      <!-- Error message -->
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
      
      <!-- Help text -->
      <div v-if="!hasError" class="help-text">
        <div class="help-content">
          <p>Tidal IDs are typically 8-10 digit numbers (e.g., 123456789)</p>
        </div>
      </div>
    </div>
    
    <!-- Tidal preview link -->
    <div v-if="localValue && isValid && !isValidating" class="tidal-preview">
      <p class="preview-text">
        Preview: 
        <a 
          :href="tidalUrl" 
          target="_blank" 
          rel="noopener noreferrer"
          class="tidal-link"
        >
          🎧 Listen on Tidal
        </a>
      </p>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue'
import { useTidalValidation } from '../../composables/useTidalValidation.js'

export default {
  name: 'TidalIdField',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    error: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'validate', 'focus', 'blur'],
  setup(props, { emit }) {
    const localValue = ref(props.modelValue?.toString() || '')
    const isFocused = ref(false)
    const hasBeenModified = ref(false)
    const { validateTidalId, isValidating } = useTidalValidation()
    
    // Generate unique field ID
    const fieldId = `tidal-id-${Math.random().toString(36).substr(2, 9)}`
    
    // Computed properties
    
    const hasError = computed(() => 
      !!(props.error || errorMessage.value)
    )
    
    const errorMessage = computed(() => 
      props.error || (localValue.value && !isValid.value && !isValidating.value ? 'Invalid Tidal ID format' : '')
    )
    
    const isValid = computed(() => {
      if (!localValue.value) return true // Empty is valid (optional field)
      return validateTidalIdFormat(localValue.value)
    })
    
    const tidalUrl = computed(() => {
      if (localValue.value && isValid.value) {
        return `https://tidal.com/browse/album/${localValue.value}`
      }
      return null
    })
    
    // Validation function
    const validateTidalIdFormat = (value) => {
      if (!value) return true // Empty is valid
      
      // Tidal IDs are typically 8-10 digit numbers
      const tidalIdPattern = /^\d{8,10}$/
      return tidalIdPattern.test(value.toString())
    }
    
    // Event handlers
    const handleInput = async () => {
      // Emit the new value
      emit('update:modelValue', localValue.value)
      
      // Perform validation
      await validateField()
    }
    
    const handleFocus = () => {
      isFocused.value = true
      emit('focus')
    }
    
    const handleBlur = async () => {
      isFocused.value = false
      emit('blur')
      
      // Perform final validation on blur
      await validateField()
    }
    
    const validateField = async () => {
      const validationResult = {
        isValid: isValid.value,
        error: errorMessage.value,
        value: localValue.value,
        field: 'tidalAlbumId'
      }
      
      // If we have a value and it's valid format, we could do additional async validation
      if (localValue.value && isValid.value && validateTidalId) {
        try {
          const asyncValidation = await validateTidalId(localValue.value)
          validationResult.isValid = asyncValidation.isValid
          if (!asyncValidation.isValid) {
            validationResult.error = asyncValidation.error || 'Invalid Tidal ID'
          }
        } catch (error) {
          console.warn('Async Tidal validation failed:', error)
          // Don't fail validation for network issues
        }
      }
      
      emit('validate', validationResult)
    }
    
    // Watch for external value changes
    watch(() => props.modelValue, (newValue) => {
      const stringValue = newValue?.toString() || ''
      if (stringValue !== localValue.value) {
        localValue.value = stringValue
      }
    })
    
    // Perform initial validation
    nextTick(() => {
      validateField()
    })
    
    return {
      localValue,
      fieldId,
      isFocused,
      hasBeenModified,
      hasError,
      errorMessage,
      isValid,
      isValidating,
      tidalUrl,
      handleInput,
      handleFocus,
      handleBlur,
      validateField
    }
  }
}
</script>

<style scoped>
/* Import tier colors */
@import '../../styles/tier-colors.css';

.tidal-id-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #f3f3f3;
  margin-bottom: 0.5rem;
}

.optional-indicator {
  font-size: 0.75rem;
  font-weight: 400;
  color: #aaa;
  font-style: italic;
}

.tidal-input-wrapper {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #f3f3f3;
  font-size: 0.875rem;
  transition: var(--tier-transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: var(--tier-d-color);
  box-shadow: 0 0 0 2px rgba(0, 191, 255, 0.2);
}

.form-input.error {
  border-color: var(--tier-s-color);
  background: rgba(255, 69, 0, 0.05);
}

.form-input::placeholder {
  color: #666;
  font-style: italic;
}

/* Status indicators */
.input-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  min-height: 1.5rem;
}

.validating-indicator,
.valid-indicator {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
}

.valid-badge {
  background: rgba(50, 205, 50, 0.2);
  color: var(--tier-c-color);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-weight: 500;
  border: 1px solid rgba(50, 205, 50, 0.3);
}

.validating-indicator {
  color: var(--tier-d-color);
}

.validating-text {
  font-style: italic;
}

.loading-spinner {
  width: 0.75rem;
  height: 0.75rem;
  border: 1.5px solid transparent;
  border-top: 1.5px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Error message */
.error-message {
  color: var(--tier-s-color);
  font-size: 0.75rem;
  font-weight: 500;
  margin-top: 0.375rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.error-message::before {
  content: '⚠️';
}

/* Help text */
.help-text {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #999;
  line-height: 1.4;
}

.help-content p {
  margin: 0 0 0.25rem 0;
}

.help-content p:last-child {
  margin-bottom: 0;
}


/* Tidal preview */
.tidal-preview {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

.preview-text {
  margin: 0;
  font-size: 0.875rem;
  color: #ccc;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tidal-link {
  color: #00bfff;
  text-decoration: none;
  font-weight: 500;
  transition: var(--tier-transition-fast);
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.tidal-link:hover {
  color: var(--tier-d-light);
  text-decoration: underline;
}

.tidal-link:focus {
  outline: 2px solid var(--tier-d-color);
  outline-offset: 2px;
  border-radius: 2px;
}

/* Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive design */
@media (max-width: 768px) {
  .form-input {
    font-size: 1rem; /* Prevent zoom on iOS */
  }
  
  .tidal-preview {
    padding: 0.5rem;
  }
  
  .preview-text {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .form-input {
    border-width: 2px;
  }
  
  .valid-badge {
    border-width: 2px;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .form-input,
  .tidal-link {
    transition: none;
  }
  
  .loading-spinner {
    animation: none;
  }
}

/* Dark mode enhancements */
.form-input:disabled {
  background: rgba(255, 255, 255, 0.02);
  border-color: rgba(255, 255, 255, 0.1);
  color: #666;
  cursor: not-allowed;
}

/* Focus management */
.form-input:focus-visible {
  outline: 2px solid var(--tier-d-color);
  outline-offset: 2px;
}
</style>